import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { LayoutDashboard, Bell, Receipt, Activity, TrendingUp, Users, ExternalLink, Loader2, RefreshCw, Folder, ChevronRight } from 'lucide-react';

const STATE_META = {
  prospectado:     { label: 'Prospectado',    color: '#94a3b8' },
  en_negociacion:  { label: 'En negociación', color: '#3b82f6' },
  contratado:      { label: 'Contratado',     color: '#A3FF12' },
  en_construccion: { label: 'En construcción',color: '#f97316' },
  instalado:       { label: 'Instalado',      color: '#a855f7' },
  activo:          { label: 'Activo',         color: '#10b981' },
  mantenimiento:   { label: 'Mantenimiento',  color: '#06b6d4' },
  conflictivo:     { label: 'Conflictivo',    color: '#ef4444' },
  baja:            { label: 'Baja',           color: '#6b7280' },
};
const PROJECT_TYPE_LABELS = {
  negociacion:'Negociación', contrato:'Contrato', tecnica:'Adecuación técnica',
  permisos:'Permisos', mantenimiento:'Mantenimiento', auditoria:'Auditoría', otros:'Otros',
};
const money = (n) => n == null ? '0 €' : `${Number(n).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €`;

const DashboardAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/dashboard');
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-[#A3FF12]" size={32}/>
      </div>
    );
  }

  const monthName = new Date(data.month_start).toLocaleDateString('es-ES', {month: 'long', year: 'numeric'});
  const statesTotal = Object.values(data.sites.by_state).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dashboard-admin-page">
      <div className="max-w-7xl mx-auto p-4 sm:p-8">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-widest text-[#A3FF12]">ADMIN · DASHBOARD GLOBAL</div>
            <h1 className="text-3xl sm:text-4xl font-black mt-1 inline-flex items-center gap-3">
              <LayoutDashboard className="text-[#A3FF12]"/> Tablero de mando
            </h1>
            <p className="text-white/50 text-sm mt-2 capitalize">Vista de {monthName}</p>
          </div>
          <button onClick={load} className="border border-white/10 rounded-lg px-3 py-2 text-xs font-bold hover:bg-white/5 inline-flex items-center gap-1">
            <RefreshCw size={12}/> Refrescar
          </button>
        </header>

        {/* TOP KPI ROW */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <BigKpi label="Sites gestionados" value={data.sites.total_managed} accent="green" icon={LayoutDashboard} to="/app/ofrecimientos"/>
          <BigKpi label="Facturas pendientes" value={money(data.invoices.pending_amount)} sub={`${data.invoices.pending_count} facturas`} accent="orange" icon={Receipt} to="/app/facturacion"/>
          <BigKpi label={`Cobrado ${monthName}`} value={money(data.invoices.paid_month_amount)} sub={`${data.invoices.paid_month_count} facturas`} accent="green" icon={TrendingUp} to="/app/facturacion"/>
          <BigKpi label="Push entregados" value={data.push.delivered} sub={`${data.push.delivery_rate}% tasa entrega`} accent="cyan" icon={Bell} to="/app/push-log"/>
        </div>

        {/* SAR JOBS SALUD 24H — alerta cuando hay errores */}
        {data.sar && (data.sar.last_24h.error > 0 || data.sar.last_24h.total > 0) && (
          <div className={`mb-6 rounded-2xl p-4 border ${data.sar.last_24h.error > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-white/5 border-white/10'}`}
            data-testid="sar-health-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className={`text-[10px] font-black tracking-widest ${data.sar.last_24h.error > 0 ? 'text-red-300' : 'text-[#A3FF12]'}`}>
                  SAR · SALUD 24H
                </div>
                <div className="text-lg font-black mt-1">
                  {data.sar.last_24h.done} ok · {data.sar.last_24h.running} en curso ·{' '}
                  <span className={data.sar.last_24h.error > 0 ? 'text-red-300' : 'text-white/60'}>{data.sar.last_24h.error} error</span>
                </div>
              </div>
              {data.sar.last_24h.error > 0 && (
                <div className="text-[11px] text-red-300/80 max-w-md">
                  <b className="text-red-300">Alerta:</b> hay jobs SAR que no llegaron a completarse. Suele indicar reciclado del pod. Considera subir el tier o revisar Gemini/red externa.
                </div>
              )}
            </div>
            {data.sar.recent_errors?.length > 0 && (
              <div className="mt-3 space-y-1">
                {data.sar.recent_errors.slice(0, 3).map((e) => (
                  <div key={e.id} className="text-[11px] bg-black/30 rounded px-2 py-1 flex items-center gap-2">
                    <span className="text-white/40 font-mono">{e.updated_at?.slice(5, 16)}</span>
                    <span className="text-white/80 truncate flex-1">{e.input?.address || 'sin dirección'}</span>
                    <span className="text-red-300 truncate max-w-xs">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* SITE STATES */}
          <Card title="Estados de site" icon={Activity} testid="sites-by-state">
            {statesTotal === 0 ? (
              <Empty text="Aún no hay sites con estado asignado."/>
            ) : (
              <div className="space-y-2">
                {Object.entries(data.sites.by_state).sort((a, b) => b[1] - a[1]).map(([state, count]) => {
                  const meta = STATE_META[state] || { label: state, color: '#94a3b8' };
                  const pct = Math.round((count / statesTotal) * 100);
                  return (
                    <div key={state}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="font-bold" style={{color: meta.color}}>{meta.label}</span>
                        <span className="text-white/60">{count} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{width: `${pct}%`, backgroundColor: meta.color}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* PUSH THIS MONTH */}
          <Card title={`Push · ${monthName}`} icon={Bell} testid="push-summary">
            <div className="grid grid-cols-4 gap-2 mb-3 text-center">
              <MiniStat label="Total" value={data.push.month_total}/>
              <MiniStat label="OK" value={data.push.delivered} color="text-[#A3FF12]"/>
              <MiniStat label="Fallo" value={data.push.failed} color="text-red-300"/>
              <MiniStat label="Exp" value={data.push.expired} color="text-white/50"/>
            </div>
            <Link to="/app/push-log" className="text-[10px] font-bold text-[#A3FF12] hover:underline inline-flex items-center gap-1">
              Ver histórico completo <ExternalLink size={10}/>
            </Link>
            <div className="mt-3 space-y-1 border-t border-white/5 pt-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-white/40">Últimos 5</div>
              {data.push.recent.length === 0 ? (
                <div className="text-white/40 text-xs italic py-2">Sin envíos.</div>
              ) : data.push.recent.map((p, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] py-0.5">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${p.status === 'delivered' ? 'bg-[#A3FF12]' : p.status === 'failed' ? 'bg-red-400' : 'bg-white/30'}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{p.title}</div>
                    <div className="text-white/40 text-[10px] truncate">{p.user_name} · {new Date(p.sent_at).toLocaleDateString('es-ES')}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* PROJECTS BY TYPE */}
          <Card title="Proyectos por tipo" icon={Folder} testid="projects-by-type">
            {data.projects.total === 0 ? (
              <Empty text="Aún no hay proyectos creados."/>
            ) : (
              <>
                <div className="text-4xl font-black text-white mb-2">{data.projects.total}</div>
                <div className="text-xs text-white/50 mb-3">proyectos activos en todos los sites</div>
                <div className="space-y-1.5">
                  {Object.entries(data.projects.by_type).sort((a, b) => b[1] - a[1]).map(([t, c]) => (
                    <div key={t} className="flex justify-between text-xs">
                      <span className="text-white/70">{PROJECT_TYPE_LABELS[t] || t}</span>
                      <span className="text-white font-bold">{c}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>

        {/* BOTTOM ROWS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Recent pending invoices */}
          <Card title="Facturas pendientes recientes" icon={Receipt} testid="recent-invoices" linkTo="/app/facturacion" linkText="Ver todas">
            {data.invoices.recent_pending.length === 0 ? (
              <Empty text="Sin facturas pendientes."/>
            ) : (
              <div className="space-y-2">
                {data.invoices.recent_pending.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-2 border border-white/5 bg-black/30 rounded-lg p-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-bold truncate">{inv.supplier}</div>
                      <div className="text-white/50 text-xs truncate">{inv.concept || '—'}</div>
                    </div>
                    <div className="text-orange-300 font-black text-sm">{money(inv.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent state changes */}
          <Card title="Cambios de estado (7d)" icon={Activity} testid="recent-state-changes" sub={`${data.activity.active_users_7d} usuarios activos`}>
            {data.activity.recent_state_changes.length === 0 ? (
              <Empty text="Sin movimientos esta semana."/>
            ) : (
              <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                {data.activity.recent_state_changes.map((c, i) => {
                  const from = STATE_META[c.from_state]?.label || c.from_state || '—';
                  const to = STATE_META[c.state]?.label || c.state;
                  const color = STATE_META[c.state]?.color || '#94a3b8';
                  return (
                    <div key={i} className="flex items-start gap-2 text-[11px] border-b border-white/5 last:border-b-0 py-1">
                      <ChevronRight size={12} className="text-white/30 mt-0.5"/>
                      <div className="flex-1 min-w-0">
                        <div>
                          <span className="text-white/40 font-mono">{c.code}</span>{' '}
                          <span className="text-white/50">· {from} → </span>
                          <b style={{color}}>{to}</b>
                        </div>
                        {c.note && <div className="text-white/50 italic truncate">{c.note}</div>}
                        <div className="text-white/30 text-[10px]">{new Date(c.state_updated_at).toLocaleDateString('es-ES')} · {c.changed_by_name || '—'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

const BigKpi = ({ label, value, sub, accent, icon: Icon, to }) => {
  const cls = {
    green: 'text-[#A3FF12] border-[#A3FF12]/30 bg-[#A3FF12]/5',
    orange:'text-orange-300 border-orange-500/30 bg-orange-500/5',
    cyan:  'text-cyan-300 border-cyan-500/30 bg-cyan-500/5',
    red:   'text-red-300 border-red-500/30 bg-red-500/5',
  }[accent] || 'text-white border-white/10 bg-white/5';
  const el = (
    <div className={`border rounded-xl p-4 ${cls}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{label}</div>
        <Icon size={16} className="opacity-70"/>
      </div>
      <div className="text-2xl sm:text-3xl font-black mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-90 transition-opacity">{el}</Link> : el;
};

const Card = ({ title, icon: Icon, children, testid, sub, linkTo, linkText }) => (
  <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon size={14} className="text-[#A3FF12]"/>}
      <h3 className="text-xs font-black uppercase tracking-widest text-white/70">{title}</h3>
      {sub && <span className="text-[10px] text-white/40 ml-1">· {sub}</span>}
      {linkTo && (
        <Link to={linkTo} className="ml-auto text-[10px] font-bold text-[#A3FF12] hover:underline inline-flex items-center gap-1">
          {linkText} <ExternalLink size={10}/>
        </Link>
      )}
    </div>
    {children}
  </div>
);

const MiniStat = ({ label, value, color = 'text-white' }) => (
  <div className="bg-black/40 border border-white/5 rounded-lg py-1.5">
    <div className={`font-black text-lg ${color}`}>{value}</div>
    <div className="text-[9px] font-bold uppercase tracking-widest text-white/40">{label}</div>
  </div>
);

const Empty = ({ text }) => <div className="text-center text-white/40 text-xs italic py-4">{text}</div>;

export default DashboardAdmin;
